import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { requestToken } from "../auth/AuthServices";
import HeaderFarms from "../utils/header_farms";
import API from "../services";
import MobileLayout from "./MobileLayout";
import QRModal from "../features/home/components/QRModal";

export interface PendingOutletContext {
  pendingCount: number
  refreshPendingCount: () => Promise<void>
}

interface PendingCountResponse {
  data?: {
    count?: number
  }
}

interface ApiErrorLike {
  response?: {
    status?: number
  }
}

const GlobalLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const pathnameRef = useRef(location.pathname);
  
  const [scrollDirection, setScrollDirection] = useState<"up" | "down">("up");
  const [pendingCount, setPendingCount] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  
  const showBottomNavRoutes = [
    '/home',
    '/profile',
  ];

  const shouldShowBottomNav = showBottomNavRoutes.some(path => location.pathname === path || location.pathname.startsWith(`${path}/`));

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    let lastScrollTop = 0;
    const handleScroll = () => {
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (currentScrollTop > lastScrollTop) {
        setScrollDirection("down");
      } else {
        setScrollDirection("up");
      }
      lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const refreshPendingCount = useCallback(async () => {
    try {
      const accessToken = await requestToken(false);

      if (!accessToken) {
        return;
      }

      const response = await API.workflowPendingCount(HeaderFarms(accessToken), {
        status: "Waiting Approval",
      }) as PendingCountResponse;

      setPendingCount(response?.data?.count ?? 0);
    } catch (e: unknown) {
      const statusCode = (e as ApiErrorLike)?.response?.status;

      if ((statusCode === 400 || statusCode === 404) && pathnameRef.current === "/home") {
        navigate("/completed-user");
        return;
      }

      console.warn("Failed fetch pending count", e);
    }
  }, [navigate]);

  useEffect(() => {
    if (accounts.length > 0) {
      void refreshPendingCount();
    }
  }, [accounts, refreshPendingCount]);

  const outletContext: PendingOutletContext = {
    pendingCount,
    refreshPendingCount,
  };

  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => setIsModalVisible(false);

  return (
    <MobileLayout
        showBottomNav={shouldShowBottomNav}
        scrollDirection={scrollDirection}
        onNavigate={navigate}
        onShowModal={showModal}
        pendingList={pendingCount}
    >
        <Outlet context={outletContext} />
        <QRModal visible={isModalVisible} onCancel={handleCancel} />
    </MobileLayout>
  );
};

export default GlobalLayout;
